import { useEffect, useState } from "react";
import { Loader2, Wallet, AlertTriangle, Receipt, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { orgToday } from "@/lib/tz";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const money = (n) => (n == null ? "—" : Number(n).toLocaleString("en-AU", { style: "currency", currency: "AUD" }));
const kmFmt = (n) => `${Number(n || 0).toLocaleString("en-AU", { maximumFractionDigits: 1 })} km`;
const fmtD = (s) => (s ? new Date(`${String(s).slice(0, 10)}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");

const STATUS_STYLE = {
  "On target": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Under budget": "bg-sky-50 text-sky-700 border-sky-200",
  "Over budget": "bg-rose-50 text-rose-700 border-rose-200",
  "No funding": "bg-rose-50 text-rose-700 border-rose-200",
  Billed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Not billed": "bg-amber-50 text-amber-700 border-amber-200",
  Available: "bg-slate-50 text-slate-600 border-slate-200",
  "Used up": "bg-rose-50 text-rose-700 border-rose-200",
};
const STATUS_HINT = {
  "On target": "Invoiced, approved and rostered to the end of the agreement come to 90-100% of the allocation.",
  "Under budget": "Invoiced, approved and rostered to the end of the agreement come to less than 90% of the allocation.",
  "Over budget": "Invoiced, approved and rostered to the end of the agreement come to more than the allocation.",
  "No funding": "Shifts are using a support category that isn't in the agreement.",
  Billed: "The establishment fee has been billed.",
  "Not billed": "The establishment fee hasn't been recorded as billed yet.",
  Available: "Balance left in the unscheduled hours pool.",
  "Used up": "The unscheduled hours pool has no balance left.",
};

function StatusPill({ status }) {
  return <span title={STATUS_HINT[status]} className={`inline-flex items-center text-[10px] font-medium border rounded-full px-2 py-0.5 whitespace-nowrap ${STATUS_STYLE[status] || ""}`}>{status}</span>;
}

// Stacked bar: invoiced, approved (not yet invoiced), rostered, against the allocation.
function UsageBar({ line }) {
  const base = Math.max(line.allocated, line.spent + line.approved + (line.awaiting || 0) + line.rostered, 1);
  const w = (v) => `${Math.min(100, (100 * v) / base)}%`;
  return (
    <div className="h-2 w-full min-w-[96px] rounded bg-slate-100 overflow-hidden flex" aria-label={`${line.percent_used ?? 0}% used`}>
      <div className="bg-brand" style={{ width: w(line.spent) }} title="Invoiced" />
      <div className="bg-amber-400" style={{ width: w(line.approved) }} title="Approved, not yet invoiced" />
      <div className="bg-violet-400" style={{ width: w(line.awaiting || 0) }} title="Worked, awaiting approval" />
      <div className="bg-sky-300" style={{ width: w(line.rostered) }} title="Rostered" />
    </div>
  );
}

// Office-only view of how the participant's active service agreement is being used.
export function ParticipantBudget({ participant, canEdit }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [version, setVersion] = useState(0);
  const [feeOpen, setFeeOpen] = useState(false);
  const [fee, setFee] = useState({ billed_on: "", amount: "", reference: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    setError(false);
    api.participantBudget(participant).then((d) => live && setData(d)).catch(() => live && setError(true));
    return () => { live = false; };
  }, [participant, version]);

  const openFee = (line) => { setFee({ billed_on: orgToday(), amount: String(line.allocated), reference: "" }); setFeeOpen(true); };
  const saveFee = async () => {
    setSaving(true);
    try {
      await api.recordEstablishmentFee(data.agreement.id, { billed_on: fee.billed_on, amount: Number(fee.amount), reference: fee.reference });
      toast.success("Establishment fee recorded as billed.");
      setFeeOpen(false); setVersion((v) => v + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
    finally { setSaving(false); }
  };
  const clearFee = async () => {
    try { await api.clearEstablishmentFee(data.agreement.id); toast.success("Establishment fee record removed."); setVersion((v) => v + 1); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove."); }
  };

  if (error) return <div className="text-sm text-rose-600" data-testid="budget-error">Couldn't load the budget.</div>;
  if (!data) return <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  if (!data.tracked) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-600" data-testid="budget-not-tracked">
        <div className="font-semibold text-slate-800 flex items-center gap-2 mb-1"><Wallet className="h-4 w-4" />No budget tracking</div>
        {data.reason} Billing carries on as normal.
      </div>
    );
  }
  const { totals, lines, agreement } = data;

  return (
    <div className="space-y-4" data-testid="participant-budget">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Wallet className="h-4 w-4" />Service agreement budget</div>
            <div className="text-[11px] text-slate-500">{fmtD(agreement.start_date)} to {fmtD(agreement.end_date)} · {data.elapsed_percent}% of the agreement has gone by · projected {money(totals.projected)} by the end</div>
          </div>
          <StatusPill status={totals.status} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mt-3">
          {[["Total", totals.allocated], ["Invoiced", totals.spent], ["Approved, not invoiced", totals.approved],
            ["Worked, awaiting approval", totals.awaiting], ["Still to be worked", totals.rostered], ["Remaining", totals.remaining]].map(([l, v]) => (
            <div key={l}>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{l}</div>
              <div className={`text-lg font-bold ${l === "Remaining" && v < 0 ? "text-rose-700" : "text-slate-800"}`}>{money(v)}</div>
            </div>))}
        </div>
        <div className="mt-3"><UsageBar line={totals} /></div>
        <div className="flex gap-4 mt-1.5 text-[10px] text-slate-500">
          <span><span className="inline-block h-2 w-2 rounded-sm bg-brand mr-1" />Invoiced</span>
          <span><span className="inline-block h-2 w-2 rounded-sm bg-amber-400 mr-1" />Approved, not invoiced</span>
          <span><span className="inline-block h-2 w-2 rounded-sm bg-violet-400 mr-1" />Worked, awaiting approval</span>
          <span><span className="inline-block h-2 w-2 rounded-sm bg-sky-300 mr-1" />Still to be worked</span>
        </div>
      </div>

      {data.needs_new_agreement.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 flex gap-2" data-testid="budget-needs-sa">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>This agreement may need replacing or topping up. Over budget or unfunded: {data.needs_new_agreement.join(", ")}.</div>
        </div>
      )}

      {data.past_without_timesheet > 0 && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="budget-past-no-timesheet">
          {data.past_without_timesheet} shift{data.past_without_timesheet === 1 ? " dated in the past has" : "s dated in the past have"} no timesheet yet. {data.past_without_timesheet === 1 ? "It is" : "They are"} still counted under "Still to be worked" until a timesheet is submitted.
        </div>
      )}

      {(data.flagged || []).length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 text-sm" data-testid="budget-flagged">
          <div className="font-semibold text-slate-800 mb-1">Shifts rostered past the agreement</div>
          <ul className="divide-y divide-amber-100">
            {data.flagged.map((f) => (
              <li key={`${f.shift}-${f.category}`} className="py-1 flex flex-wrap gap-x-3 text-xs text-slate-600">
                <span className="font-medium text-slate-800">{f.shift}</span><span>{fmtD(f.date)}</span><span>{f.category}</span>
                <span>{f.action === "override" ? "Overridden" : "New agreement needed"}{f.funded_from ? `, funded from ${f.funded_from}` : ""}{f.by ? ` by ${f.by}` : ""}{f.reason ? `: ${f.reason}` : ""}</span>
              </li>))}
          </ul>
        </div>
      )}

      {data.transport && (() => {
        const t = data.transport;
        const stat = (label, value, tone) => (
          <div><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div><div className={`text-lg font-bold ${tone || "text-slate-800"}`}>{value}</div></div>);
        const leftAfter = t.allocated - t.projected;
        return (
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="budget-transport">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-semibold text-slate-800">Activity based transport (km)</div>
              <StatusPill status={t.status} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-3">
              {stat("Allocated", kmFmt(t.allocated))}
              {stat("Claimed so far", kmFmt(t.claimed))}
              {stat("Authorised, still to be worked", kmFmt(t.authorised))}
              {stat("Projected total", kmFmt(t.projected))}
              {stat(leftAfter >= 0 ? "Left after that" : "Over by", kmFmt(Math.abs(leftAfter)), leftAfter >= 0 ? "text-emerald-700" : "text-rose-700")}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-3 pt-3 border-t border-slate-100">
              {stat("Expected by now", kmFmt(t.expected_to_date))}
              {stat(t.surplus >= 0 ? "Surplus so far" : "Over so far", kmFmt(Math.abs(t.surplus)), t.surplus >= 0 ? "text-emerald-700" : "text-rose-700")}
              {t.avg_authorised != null && stat("Authorised per worked shift", kmFmt(t.avg_authorised))}
              {t.avg_claimed != null && stat("Claimed per worked shift", kmFmt(t.avg_claimed))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Shifts that have been worked count what was actually claimed, not what was authorised. Km authorised on shifts still to be worked count as committed, so the status and "Projected total" include them. "Surplus so far" compares claimed km with an even share of the allocation for the time that has gone by, so it can be a surplus even when the projected total is over.
            </p>
          </div>);
      })()}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>{["Support category", "Allocated", "Invoiced", "Approved", "Awaiting approval", "Still to be worked", "Remaining", "Used", "Status"].map((h) => <th key={h} className="text-left font-semibold px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.category} className="border-t border-slate-100" data-testid={`budget-line-${l.category}`}>
                <td className="px-3 py-2 font-medium text-slate-800">{l.kind === "transport" ? "Activity based transport (km)" : l.category}{!l.in_agreement && <span className="ml-1.5 text-[10px] text-rose-600">not in agreement</span>}
                  {l.kind === "fee" && canEdit && (l.status === "Billed"
                    ? <button onClick={clearFee} className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-normal text-slate-400 hover:text-rose-600" data-testid="budget-fee-clear"><Undo2 className="h-3 w-3" />undo</button>
                    : <button onClick={() => openFee(l)} className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-normal text-brand hover:underline" data-testid="budget-fee-record"><Receipt className="h-3 w-3" />Record as billed</button>)}
                  {l.kind === "fee" && data.establishment_fee_billed && <div className="text-[10px] font-normal text-slate-400">Billed {fmtD(data.establishment_fee_billed.billed_on)}{data.establishment_fee_billed.reference ? ` · ${data.establishment_fee_billed.reference}` : ""}</div>}
                </td>
                {l.kind === "transport" ? (
                  <>
                    <td className="px-3 py-2 tabular-nums">{kmFmt(l.km.allocated)}<div className="text-[10px] text-slate-400">{money(l.allocated)}</div></td>
                    <td className="px-3 py-2 tabular-nums">{kmFmt(l.km.invoiced)}</td>
                    <td className="px-3 py-2 tabular-nums">{kmFmt(l.km.approved)}</td>
                    <td className="px-3 py-2 tabular-nums">{kmFmt(l.km.awaiting)}</td>
                    <td className="px-3 py-2 tabular-nums">{kmFmt(l.km.authorised)}</td>
                    <td className={`px-3 py-2 tabular-nums ${l.km.remaining < 0 ? "text-rose-700 font-semibold" : ""}`}>{kmFmt(l.km.remaining)}<div className="text-[10px] text-slate-400">{money(l.remaining)}</div></td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-2 tabular-nums">{money(l.allocated)}</td>
                    <td className="px-3 py-2 tabular-nums">{money(l.spent)}</td>
                    <td className="px-3 py-2 tabular-nums">{money(l.approved)}</td>
                    <td className="px-3 py-2 tabular-nums">{money(l.awaiting)}</td>
                    <td className="px-3 py-2 tabular-nums">{money(l.rostered)}</td>
                    <td className={`px-3 py-2 tabular-nums ${l.remaining < 0 ? "text-rose-700 font-semibold" : ""}`}>{money(l.remaining)}</td>
                  </>
                )}
                <td className="px-3 py-2 min-w-[120px]"><UsageBar line={l} /><div className="text-[10px] text-slate-400 mt-0.5">{l.percent_used == null ? "—" : `${l.percent_used}%`}</div></td>
                <td className="px-3 py-2"><StatusPill status={l.status} /></td>
              </tr>))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">"Invoiced" is what has been invoiced and "Approved" is approved but not yet invoiced; both use the approved amounts. "Awaiting approval" is worked shifts whose timesheet isn't approved yet, and "Still to be worked" is shifts still to come (and past ones with no timesheet). The last three are committed, so they count against the balance; SIL weekly weeks not yet invoiced show under "Still to be worked". The status compares the projected total (everything invoiced, approved or rostered to the end of the agreement) with the allocation. Cancelled shifts are left out. The establishment fee is billed outside this system, so record it when it's billed.</p>

      <Dialog open={feeOpen} onOpenChange={setFeeOpen}>
        <DialogContent data-testid="budget-fee-dialog">
          <DialogHeader><DialogTitle className="font-brand text-base">Record establishment fee as billed</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <label className="text-[11px] font-semibold text-slate-600">Date billed<Input type="date" value={fee.billed_on} onChange={(e) => setFee((f) => ({ ...f, billed_on: e.target.value }))} className="mt-1 h-9 text-sm" /></label>
            <label className="text-[11px] font-semibold text-slate-600">Amount billed ($)<Input type="number" min="0" step="0.01" value={fee.amount} onChange={(e) => setFee((f) => ({ ...f, amount: e.target.value }))} className="mt-1 h-9 text-sm" /></label>
            <label className="text-[11px] font-semibold text-slate-600">Reference (optional)<Input value={fee.reference} onChange={(e) => setFee((f) => ({ ...f, reference: e.target.value }))} placeholder="Invoice or claim number" className="mt-1 h-9 text-sm" /></label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeeOpen(false)}>Cancel</Button>
            <Button onClick={saveFee} disabled={saving || !fee.billed_on || !(Number(fee.amount) > 0)} style={{ backgroundColor: "var(--brand)" }} data-testid="budget-fee-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
